/**
 * Indicates the origin of control-room financial data for a given date:
 * - 'live': computed from current live tables (today)
 * - 'snapshot': read from a historical snapshot table (stat_index / stat_au24k)
 * - 'calculated': reconstructed for a historical date that has no snapshot
 */
export type DataSource = 'live' | 'snapshot' | 'calculated';

export interface FinancialFlow {
    activ: number;
    pasiv: number;
    expirat: number;
    marfa: number;
    /** Contracts behind each row; null or absent when the day's snapshot did not record it. */
    activCount?: number | null;
    pasivCount?: number | null;
    expiratCount?: number | null;
    marfaCount?: number | null;
}

/** A Flux card row; the key is also its label's translation key under `controlRoom`. */
export type FinancialFlowRowKey = 'active' | 'passive' | 'expired' | 'merchandise';

export interface FinancialFlowFigures {
    euro: number;
    lei: number;
    count: number | null;
}

export interface FinancialFlowRow extends FinancialFlowFigures {
    key: FinancialFlowRowKey;
}

type FlowAmountField = 'activ' | 'pasiv' | 'expirat' | 'marfa';

const FINANCIAL_FLOW_ROW_FIELDS: ReadonlyArray<
    readonly [FinancialFlowRowKey, FlowAmountField, `${FlowAmountField}Count`]
> = [
    ['active', 'activ', 'activCount'],
    ['passive', 'pasiv', 'pasivCount'],
    ['expired', 'expirat', 'expiratCount'],
    ['merchandise', 'marfa', 'marfaCount'],
];

const sumCounts = (rows: FinancialFlowRow[]): number | null =>
    rows.reduce<number | null>(
        (total, row) => (total === null || row.count === null ? null : total + row.count),
        0,
    );

/**
 * The Flux card's four rows and their total: the EUR value, the same in lei at the day's rate
 * (1 without one, as the card always did) and the number of contracts. The total's count is
 * unknown as soon as one row's is.
 */
export const buildFinancialFlowRows = (
    flow: FinancialFlow,
    exchangeRate: number | null,
): { rows: FinancialFlowRow[]; total: FinancialFlowFigures } => {
    const rate = Number(exchangeRate ?? 1);
    const rows = FINANCIAL_FLOW_ROW_FIELDS.map(([key, amountField, countField]) => {
        const euro = Number(flow[amountField]);
        return { key, euro, lei: euro * rate, count: flow[countField] ?? null };
    });
    const euro = rows.reduce((total, row) => total + row.euro, 0);
    return { rows, total: { euro, lei: euro * rate, count: sumCounts(rows) } };
};
export interface GrandTotals {
    lei: number;
    euro: number;
    exchangeRate: number | null;
}

export interface Au24KData {
    grams: number;
    percentage: number;
    activTotal: number;
}

export interface FinanceNote {
    id: number;
    denumire: string;
    suma: number;
    data: string;
}

export interface DailyBalance {
    ziua: string;
    data: string;
    nrContracte: number;
    valContracte: number;
    nrContractePlatite: number;
    valContractePlatite: number;
    comContractePlatite: number;
    nrComisioanePlatite: number;
    valComisioanePlatite: number;
}

export interface DailyStats {
    venit: number;
    profit: number;
    aur585: number;
    aur750: number;
    alteCarataje: number;
    inventarLunar: number;
    totalComisiiZi: number;
}

export interface FinanceGroupData {
    id: number;
    denumire: string;
    valoare: number;
    indexBaza: number;
    source?: DataSource;
}

export interface Au24KBreakdownRow {
    category: string;
    GR: number;
    RON: number;
    source?: DataSource;
}

export interface DeliveredNotInvoiced {
    id: number;
    nrContract: string;
    nume: string;
    obiect: string;
    data: string;
}

export interface FinancialItem {
    id: number;
    denumire: string;
    suma: number;
    data: string;
    stamp: string;
    isSumed: boolean;
    ordern: number;
    automated: boolean;
    fixed: boolean;
}

export interface DailyMovementsEntry {
    id: string;
    label: string;
    amount: number;
    user?: string;
    timestamp?: string;
    invoiceRef?: string;
}

export interface DailyMovementsGroup {
    type: 'intrari' | 'iesiri';
    group: string;
    entries: DailyMovementsEntry[];
    total: number;
}

export interface DailyCashCheckTotals {
    intrari: number;
    iesiri: number;
    invoices: number;
    contracts: number;
    dispozitiiIntrari: number;
    dispozitiiIesiri: number;
    transfersIn: number;
    transfersOut: number;
}

export interface DailyCashCheck {
    date: string;
    cashPointId: number | null;
    sessionNumber: number | null;
    expected: number;
    /**
     * The cash the session's till held: its live balance while open, what its handover left once
     * handed over, `null` when nobody counted it (no session, or one closed without a handover).
     */
    actual: number | null;
    /** `actual` less `expected`; `null` with `actual`. */
    difference: number | null;
    previousBalance: number;
    totals: DailyCashCheckTotals;
    intrari: DailyMovementsGroup[];
    iesiri: DailyMovementsGroup[];
}

export type DailyCashDifferenceState = 'balanced' | 'surplus' | 'shortage' | 'notCounted';

export interface ControlRoomData {
    dailyBalance: DailyBalance[];
    dailyStats: DailyStats;
    financialFlow: FinancialFlow;
    grandTotals: GrandTotals;
    au24kData: Au24KData;
    financeGroupsData: FinanceGroupData[];
    financialItems: FinancialItem[];
    financeNotes: FinanceNote[];
    deliveredNotInvoiced: DeliveredNotInvoiced[];
}

interface ControlRoomRefreshStateParams {
    selectedDateStr: string;
    fallbackDailyBalanceLabel: string;
    financialItemsResponse: { success?: boolean; data?: FinancialItemFromApi[] } | undefined;
    dailyBalanceResponse: { success?: boolean; data?: DailyBalance[] } | undefined;
    dailyStatsResponse: { success?: boolean; data?: DailyStats } | undefined;
    dailyMovementsResponse: { success?: boolean; data?: DailyMovementsGroup[] } | undefined;
    dailyCashCheckResponse: { success?: boolean; data?: DailyCashCheck } | undefined;
}

interface FinancialItemFromApi {
    id: number;
    denumire: string;
    suma: number;
    data: string;
    stamp: string;
    isSumed: boolean;
    ordern?: number;
    automated: boolean;
    fixed: boolean;
}

export const buildControlRoomRefreshState = ({
    selectedDateStr,
    fallbackDailyBalanceLabel,
    financialItemsResponse,
    dailyBalanceResponse,
    dailyStatsResponse,
    dailyMovementsResponse,
    dailyCashCheckResponse,
}: ControlRoomRefreshStateParams) => {
    const financialItems =
        financialItemsResponse?.success && financialItemsResponse.data
            ? financialItemsResponse.data
                  .map((item, index) => ({
                      ...item,
                      ordern: item.ordern ?? index + 1,
                  }))
                  .sort((a, b) => a.ordern - b.ordern)
            : [
                  {
                      id: 1,
                      denumire: '-',
                      suma: 0,
                      data: '-',
                      stamp: '-',
                      isSumed: true,
                      ordern: 1,
                      automated: false,
                      fixed: false,
                  },
                  {
                      id: 2,
                      denumire: '-',
                      suma: 0,
                      data: '-',
                      stamp: '-',
                      isSumed: true,
                      ordern: 2,
                      automated: false,
                      fixed: false,
                  },
                  {
                      id: 3,
                      denumire: '-',
                      suma: 0,
                      data: '-',
                      stamp: '-',
                      isSumed: true,
                      ordern: 3,
                      automated: false,
                      fixed: false,
                  },
              ];

    const dailyBalance =
        dailyBalanceResponse?.success && dailyBalanceResponse.data
            ? dailyBalanceResponse.data
            : [
                  {
                      ziua: fallbackDailyBalanceLabel,
                      data: selectedDateStr,
                      nrContracte: 15,
                      valContracte: 45000,
                      nrContractePlatite: 8,
                      valContractePlatite: 32000,
                      comContractePlatite: 4800,
                      nrComisioanePlatite: 3,
                      valComisioanePlatite: 1200,
                  },
              ];

    const dailyStats =
        dailyStatsResponse?.success && dailyStatsResponse.data
            ? dailyStatsResponse.data
            : {
                  venit: 12500,
                  profit: 8300,
                  aur585: 125.5,
                  aur750: 85.2,
                  alteCarataje: 45.8,
                  inventarLunar: 35000,
                  totalComisiiZi: 6000,
              };

    const dailyMovements =
        dailyMovementsResponse?.success && Array.isArray(dailyMovementsResponse.data)
            ? dailyMovementsResponse.data
            : [];

    const dailyCashCheck =
        dailyCashCheckResponse?.success && dailyCashCheckResponse.data
            ? dailyCashCheckResponse.data
            : null;

    return {
        financialItems,
        dailyBalance,
        dailyStats,
        dailyMovements,
        dailyCashCheck,
    };
};

export const getDailyCashDifferenceState = (
    difference: number | null,
    tolerance = 0.01,
): DailyCashDifferenceState => {
    if (difference === null) {
        return 'notCounted';
    }
    if (Math.abs(difference) <= tolerance) {
        return 'balanced';
    }

    return difference > 0 ? 'surplus' : 'shortage';
};

/**
 * The currencies a Control Room card can be denominated in.
 *
 * `LEI` is the label the application and Delphi both use for RON; the codes are
 * the same word in Romanian, Hungarian and English, which is why they are not
 * translated.
 */
export type ControlRoomCurrency = 'EUR' | 'LEI';

/**
 * Append the currency a card's figures are in to its title, in brackets.
 *
 * The Control Room mixes the two: the daily balance and the statistics under it
 * are kept in EUR, the way `bilant` has always stored them, while the sumator,
 * the index, the notes and every cash figure are in lei. Nothing on the page
 * said so, and the two are within a factor of five of each other, so a figure
 * read in the wrong currency looks plausible.
 *
 * A card carrying both gets both, in the order its columns appear.
 */
export const withCurrency = (title: string, ...currencies: ControlRoomCurrency[]): string =>
    `${title} (${currencies.join(' / ')})`;
